/**
 * Email Recipients Page
 * Manage email notification recipients (internal staff, investors, brokers)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Switch,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AccountBalance as InvestorIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapseIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/apiClient';
import { groupByCategory } from '../../utils/notificationCategories';
import logger from '../../utils/logger';
import SortableHeaderCell from '../../components/common/SortableHeaderCell';
import useTableSort from '../../hooks/useTableSort';
import usePagination from '../../hooks/usePagination';

// Notification types for per-recipient preferences are fetched from the API
// (single source of truth) so this list never drifts from the backend registry.

const RECIPIENT_TYPE_ICONS = {
  internal: <PersonIcon fontSize="small" />,
  investor: <InvestorIcon fontSize="small" />,
  broker: <BusinessIcon fontSize="small" />,
};

const RECIPIENT_TYPE_COLORS = {
  internal: 'primary',
  investor: 'success',
  broker: 'warning',
};

function EmailRecipientsPage() {
  const [loading, setLoading] = useState(true);
  const [recipients, setRecipients] = useState([]);
  const [tabValue, setTabValue] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const pagination = usePagination({ defaultRowsPerPage: 25 });
  const { page, rowsPerPage, resetPage } = pagination;
  const [totalCount, setTotalCount] = useState(0);
  const sort = useTableSort({ onSortChange: resetPage });
  const [message, setMessage] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);

  // Form state for add/edit
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    recipient_type: 'internal',
    is_active: true,
    preferences: {},
  });
  const [notificationTypes, setNotificationTypes] = useState([]);
  // Collapsible preference categories in the edit/add dialogs (all collapsed by default).
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());

  const toggleCategory = (category) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category); else next.add(category);
      return next;
    });
  };

  // Reset all preference categories to collapsed (called when a dialog opens).
  const collapseAllCategories = (types) => {
    setCollapsedCategories(new Set(groupByCategory(types).map(g => g.category)));
  };

  const loadRecipients = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
      });

      if (tabValue !== 'all') {
        params.append('type', tabValue);
      }

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (sort.apiParams.sort_by) {
        params.append('sort_by', sort.apiParams.sort_by);
        params.append('sort_dir', sort.apiParams.sort_dir);
      }

      const response = await apiGet(`/email/recipients?${params.toString()}`);
      if (response.success) {
        setRecipients(response.data);
        setTotalCount(response.pagination?.total || 0);
      }
    } catch (error) {
      logger.error('Error loading recipients', { message: error.message, status: error.response?.status });
      setMessage({ type: 'error', text: 'Failed to load recipients' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, tabValue, searchTerm, sort.apiParams]);

  useEffect(() => {
    loadRecipients();
  }, [loadRecipients]);

  // Fetch the notification-type registry once for the preferences UI.
  useEffect(() => {
    (async () => {
      try {
        const res = await apiGet('/email/notification-types');
        if (res.success && Array.isArray(res.data)) setNotificationTypes(res.data);
      } catch (error) {
        logger.error('Error loading notification types', { message: error.message });
      }
    })();
  }, []);

  // Auto-sync once on mount so the internal recipient list always reflects the
  // tenant's current users-with-access (authorized_users x tenant_users). The sync
  // upserts active tenant users and deactivates internal recipients whose access was
  // revoked. Failure is non-fatal — loadRecipients still runs and shows current data.
  useEffect(() => {
    (async () => {
      try {
        await apiPost('/email/recipients/sync');
      } catch (error) {
        logger.error('Auto-sync recipients on load failed', { message: error.message });
      } finally {
        loadRecipients();
      }
    })();
    // Run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await apiPost('/email/recipients/sync');
      if (response.success) {
        setMessage({
          type: 'success',
          text: `Synced ${response.synced.total} recipients (${response.synced.internal} internal, ${response.synced.investors} investors, ${response.synced.brokers} brokers)`,
        });
        loadRecipients();
      }
    } catch (error) {
      logger.error('Error syncing recipients', { message: error.message, status: error.response?.status });
      setMessage({ type: 'error', text: 'Failed to sync recipients' });
    } finally {
      setSyncing(false);
    }
  };

  const handleEditClick = (recipient) => {
    setSelectedRecipient(recipient);
    setFormData({
      email: recipient.email,
      name: recipient.name,
      recipient_type: recipient.recipient_type,
      is_active: recipient.is_active,
      preferences: recipient.preferences || {},
    });
    collapseAllCategories(getAvailableNotifications(recipient.recipient_type));
    setEditDialogOpen(true);
  };

  const handleAddClick = () => {
    setFormData({
      email: '',
      name: '',
      // Internal recipients are sync-managed from tenant users; manual add is for
      // non-internal types only.
      recipient_type: 'investor',
      is_active: true,
      preferences: {},
    });
    setAddDialogOpen(true);
  };

  const handleDeleteClick = (recipient) => {
    setSelectedRecipient(recipient);
    setDeleteDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      await apiPut(`/email/recipients/${selectedRecipient.id}`, {
        name: formData.name,
        is_active: formData.is_active,
        preferences: formData.preferences,
      });
      setMessage({ type: 'success', text: 'Recipient updated successfully' });
      setEditDialogOpen(false);
      loadRecipients();
    } catch (error) {
      logger.error('Error updating recipient', { message: error.message, status: error.response?.status });
      setMessage({ type: 'error', text: error.message || 'Failed to update recipient' });
    }
  };

  const handleSaveAdd = async () => {
    try {
      await apiPost('/email/recipients', formData);
      setMessage({ type: 'success', text: 'Recipient added successfully' });
      setAddDialogOpen(false);
      loadRecipients();
    } catch (error) {
      logger.error('Error adding recipient', { message: error.message, status: error.response?.status });
      setMessage({ type: 'error', text: error.message || 'Failed to add recipient' });
    }
  };

  const handleDelete = async () => {
    try {
      await apiDelete(`/email/recipients/${selectedRecipient.id}`);
      setMessage({ type: 'success', text: 'Recipient deleted successfully' });
      setDeleteDialogOpen(false);
      loadRecipients();
    } catch (error) {
      logger.error('Error deleting recipient', { message: error.message, status: error.response?.status });
      setMessage({ type: 'error', text: error.message || 'Failed to delete recipient' });
    }
  };

  const handleToggleActive = async (recipient) => {
    try {
      await apiPut(`/email/recipients/${recipient.id}`, {
        is_active: !recipient.is_active,
      });
      loadRecipients();
    } catch (error) {
      logger.error('Error toggling recipient status', { message: error.message, status: error.response?.status });
      setMessage({ type: 'error', text: 'Failed to update recipient' });
    }
  };

  const handlePreferenceChange = (key, checked) => {
    setFormData(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [key]: checked,
      },
    }));
  };

  const getAvailableNotifications = (recipientType) => {
    // All notification types are internal-only. Non-internal recipients have no
    // applicable preferences (they receive statements only, a separate feature).
    if (recipientType !== 'internal') {
      return notificationTypes.filter(n => !n.internalOnly);
    }
    return notificationTypes;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Email Recipients
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage email notification recipients and their preferences
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
            onClick={handleSync}
            disabled={syncing}
          >
            Sync All
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
          >
            Add Recipient
          </Button>
        </Box>
      </Box>
      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => { setTabValue(newValue); resetPage(); }}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="All" value="all" />
          <Tab
            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><PersonIcon fontSize="small" /> Internal</Box>}
            value="internal"
          />
          <Tab
            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><InvestorIcon fontSize="small" /> Investors</Box>}
            value="investor"
          />
          <Tab
            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><BusinessIcon fontSize="small" /> Brokers</Box>}
            value="broker"
          />
        </Tabs>

        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder="Search by email or name..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); resetPage(); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            size="small"
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <SortableHeaderCell sort={sort} columnKey="name">Recipient</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="recipient_type">Type</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="source_table">Source</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="is_active" align="center">Active</SortableHeaderCell>
                <SortableHeaderCell sort={sort} columnKey="updated_at">Updated</SortableHeaderCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : recipients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No recipients found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                recipients.map((recipient) => (
                  <TableRow key={recipient.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {recipient.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {recipient.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={RECIPIENT_TYPE_ICONS[recipient.recipient_type]}
                        label={recipient.recipient_type}
                        size="small"
                        color={RECIPIENT_TYPE_COLORS[recipient.recipient_type]}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {recipient.source_table === 'manual' ? 'Manual' : recipient.source_table?.replace('stakeholder_', '')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={recipient.is_active}
                        onChange={() => handleToggleActive(recipient)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {recipient.updated_at ? format(new Date(recipient.updated_at), 'dd MMM yyyy') : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit preferences">
                        <IconButton size="small" onClick={() => handleEditClick(recipient)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* Synced internal recipients are managed from tenant users — deleting
                          one is pointless (sync re-adds it), so hide Delete for them. */}
                      {recipient.source_table !== 'authorized_users' && (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDeleteClick(recipient)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination {...pagination.tableProps(totalCount)} />
      </Paper>
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Recipient</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Email"
                value={formData.email}
                disabled
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                disabled={selectedRecipient?.source_table === 'authorized_users'}
                helperText={selectedRecipient?.source_table === 'authorized_users'
                  ? 'Synced from the tenant user record — edit the user to change the name.'
                  : undefined}
              />
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Notification Preferences
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Uncheck to opt out of specific notification types
              </Typography>
              {groupByCategory(getAvailableNotifications(formData.recipient_type)).map((group) => {
                const isCollapsed = collapsedCategories.has(group.category);
                return (
                  <Box key={group.category} sx={{ mb: 1 }}>
                    <Box
                      onClick={() => toggleCategory(group.category)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', py: 0.5, fontWeight: 600 }}
                    >
                      {isCollapsed ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{group.label}</Typography>
                    </Box>
                    {!isCollapsed && (
                      <Box sx={{ pl: 3 }}>
                        {group.items.map((notif) => (
                          <FormControlLabel
                            key={notif.key}
                            control={
                              <Checkbox
                                checked={formData.preferences[notif.key] !== false}
                                onChange={(e) => handlePreferenceChange(notif.key, e.target.checked)}
                              />
                            }
                            label={notif.label}
                            sx={{ display: 'block', ml: 0 }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit}>Save</Button>
        </DialogActions>
      </Dialog>
      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Recipient</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Recipient Type</InputLabel>
                <Select
                  value={formData.recipient_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, recipient_type: e.target.value }))}
                  label="Recipient Type"
                >
                  <MenuItem value="investor">Investor</MenuItem>
                  <MenuItem value="broker">Broker</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Internal recipients sync automatically from the tenant's users — they can't be added manually here.
              </Typography>
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveAdd}
            disabled={!formData.email || !formData.name}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Recipient</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedRecipient?.email}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmailRecipientsPage;
